import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import API_BASE_URL from "./config";
import "./PortfolioPublic.css";

function PortfolioPublic() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const eventParam = searchParams.get("event"); // ?event=123

  const [studio, setStudio] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Active tab: "all" or event id
  const [activeTab, setActiveTab] = useState(eventParam || "all");
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);

  // Lightbox
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [rotation, setRotation] = useState(0);
  const [fullView, setFullView] = useState(false);
  const touchStart = React.useRef(null);

  const fetchPortfolio = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/portfolio/shared/${token}`);
      if (!res.ok) { setError("Portfolio not found"); setLoading(false); return; }
      const data = await res.json();
      setStudio(data.studio);
      setEvents(data.events || []);
      // Auto-select based on query param or default to all
      if (eventParam) {
        const evId = parseInt(eventParam, 10);
        if (data.events?.some((e) => e.id === evId)) {
          loadEventImages(evId);
          setActiveTab(String(evId));
        } else {
          loadAllImages();
        }
      } else {
        loadAllImages();
      }
    } catch {
      setError("Failed to load portfolio");
    }
    setLoading(false);
  }, [token, eventParam]);

  useEffect(() => { fetchPortfolio(); }, [fetchPortfolio]);

  const loadAllImages = async () => {
    setActiveTab("all");
    setLoadingImages(true);
    setImages([]);
    try {
      const res = await fetch(`${API_BASE_URL}/api/portfolio/shared/${token}/all-images`);
      const data = await res.json();
      setImages(data.images || []);
    } catch { /* ignore */ }
    setLoadingImages(false);
  };

  const loadEventImages = async (eventId) => {
    setActiveTab(String(eventId));
    setLoadingImages(true);
    setImages([]);
    try {
      const res = await fetch(`${API_BASE_URL}/api/portfolio/shared/${token}/event/${eventId}/images`);
      const data = await res.json();
      setImages(data.images || []);
    } catch { /* ignore */ }
    setLoadingImages(false);
  };

  // Lightbox controls
  const openLightbox = (idx) => { setLightboxIdx(idx); setRotation(0); setFullView(false); };
  const closeLightbox = () => { setLightboxIdx(-1); setRotation(0); setFullView(false); };
  const goPrev = () => { setLightboxIdx((i) => (i > 0 ? i - 1 : images.length - 1)); setRotation(0); };
  const goNext = () => { setLightboxIdx((i) => (i < images.length - 1 ? i + 1 : 0)); setRotation(0); };
  const rotateCW = () => setRotation((r) => (r - 90 + 360) % 360);
  const toggleFullView = () => setFullView((v) => !v);

  useEffect(() => {
    if (lightboxIdx < 0) return;
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "Escape") closeLightbox();
      else if (e.key === "r" || e.key === "R") rotateCW();      else if (e.key === "f" || e.key === "F") toggleFullView();    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIdx, images.length]);

  if (loading) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <p>Loading portfolio...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pp-error">
        <h1>😕</h1>
        <p>{error}</p>
      </div>
    );
  }

  const lightboxImage = lightboxIdx >= 0 ? images[lightboxIdx] : null;
  const isRotated = rotation === 90 || rotation === 270;

  return (
    <div className="pp-container">
      {/* Hero / Studio Header */}
      <header className="pp-hero">
        <div className="pp-hero-bg" />
        <div className="pp-hero-content">
          {studio?.logo_url ? (
            <img src={studio.logo_url} alt="Logo" className="pp-logo" />
          ) : (
            <div className="pp-logo-placeholder">
              {(studio?.studio_name || "P").charAt(0).toUpperCase()}
            </div>
          )}
          <h1 className="pp-studio-name">{studio?.studio_name || "Portfolio"}</h1>
          <p className="pp-tagline">Showcasing Our Finest Work</p>
        </div>
      </header>

      {/* Event Category Tabs */}
      {events.length > 0 && (
        <nav className="pp-tabs">
          <button
            className={`pp-tab ${activeTab === "all" ? "active" : ""}`}
            onClick={loadAllImages}
          >
            All
            <span className="pp-tab-count">
              {events.reduce((s, e) => s + Number(e.image_count || 0), 0)}
            </span>
          </button>
          {events.map((ev) => (
            <button
              key={ev.id}
              className={`pp-tab ${activeTab === String(ev.id) ? "active" : ""}`}
              onClick={() => loadEventImages(ev.id)}
            >
              {ev.event_type_name}
              <span className="pp-tab-count">{ev.image_count}</span>
            </button>
          ))}
        </nav>
      )}

      {/* Gallery */}
      <section className="pp-gallery-section">
        {loadingImages ? (
          <div className="pp-gallery-loading">
            <div className="pp-spinner" />
          </div>
        ) : images.length === 0 ? (
          <p className="pp-no-images">No images in this category yet.</p>
        ) : (
          <div className="pp-gallery">
            {images.map((img, idx) => (
              <div
                key={img.id}
                className="pp-card"
                onClick={() => openLightbox(idx)}
              >
                <img src={img.thumbnail_url || img.image_url} alt="" loading="lazy" />
                <div className="pp-card-overlay">
                  <span className="pp-card-zoom">🔍</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="pp-footer">
        <p>© {new Date().getFullYear()} {studio?.studio_name} • Powered by <strong>Photentra</strong></p>
      </footer>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className={`pp-lightbox ${fullView ? "pp-fullview" : ""}`}
          onClick={closeLightbox}
          onTouchStart={(e) => { touchStart.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touchStart.current === null) return;
            const diff = e.changedTouches[0].clientX - touchStart.current;
            if (diff > 60) goPrev();
            else if (diff < -60) goNext();
            touchStart.current = null;
          }}
        >
          <button className="pp-lb-close" onClick={(e) => { e.stopPropagation(); closeLightbox(); }}>✕</button>
          <button className="pp-lb-arrow pp-lb-prev" onClick={(e) => { e.stopPropagation(); goPrev(); }}>‹</button>
          <div className="pp-lb-content" onClick={(e) => e.stopPropagation()}>
            <div className="pp-lb-img-container">
              <img
                src={lightboxImage.image_url}
                alt=""
                className={`pp-lb-image ${fullView ? "pp-fullview-image" : ""}`}
                style={{
                  transform: `rotate(${rotation}deg)`,
                  maxWidth: isRotated ? (fullView ? "95vh" : "80vh") : (fullView ? "98vw" : "90vw"),
                  maxHeight: isRotated ? (fullView ? "95vw" : "90vw") : (fullView ? "92vh" : "80vh"),
                }}
              />
            </div>
            <div className={`pp-lb-bottom ${fullView ? "pp-fullview-bottom" : ""}`}>
              <button className={`pp-lb-btn ${fullView ? "pp-lb-btn-active" : ""}`} onClick={toggleFullView} title="Full View (F)">{fullView ? "⛶ Exit Full" : "⛶ Full View"}</button>
              <button className="pp-lb-btn" onClick={rotateCW} title="Rotate (R)">🔄 Rotate</button>
              <span className="pp-lb-counter">{lightboxIdx + 1} / {images.length}</span>
            </div>
          </div>
          <button className="pp-lb-arrow pp-lb-next" onClick={(e) => { e.stopPropagation(); goNext(); }}>›</button>
        </div>
      )}
    </div>
  );
}

export default PortfolioPublic;
